import React, { useState } from 'react';
import { View, Text, Pressable } from 'react-native';
import { useTranslation } from 'react-i18next';
import { styles } from 'styles/components/customCloverPointsSelect';
import { getAvailableCloverPoints } from 'services/paymentsServices';
import { cloverPointsReducer as reducer, initialStateForCloverPoints } from 'utils/report';
import CustomCloverPointSelectProps from 'types/components/CustomCloverPointSelectProps';
import { ReducerState } from 'types/models/ReducerAction';
import CloverPoint from 'types/models/CloverPoint';
import useMediumScreen from 'hooks/useMediumScreen';


export default ({
  selectedCommerceIds,
  commerces,
  venueId,
  cloverConfigIdFromVenue,
  callback,
  venueColor,
  RNMultiSelect,
}: CustomCloverPointSelectProps): JSX.Element => {
  const [pickedCloverPoints, setPickedCloverPoints] = useState<string[]>([]);
  const [state, dispatch] = React.useReducer(reducer, initialStateForCloverPoints);
  const { t } = useTranslation();
  const mediumScreen = useMediumScreen();
  

  const renderActionButton = (applyAction: () => void, type: 'clear' | 'save') => {
    return (
      <Pressable style={ mediumScreen ? styles.button : styles.buttonSmall} onPress={() => applyAction()}>
        <View style={[styles[`${mediumScreen ? '' : 'small'}${type}ButtonContainer`], type === 'save' && { backgroundColor: venueColor }]}>
          <View style={styles.actionButtonTextContainer}>
            <Text style={styles[`${type}ButtonText`]}>{t(`${type}-selection`)}</Text>
          </View>
        </View>
      </Pressable>
    );
  };

  const settingPossibleCloverPoints = React.useCallback(
    async (venueId?: string, commerceIds?: string[]) => {
      if (venueId) {
        const isVenueIdInCloverPointsState = state.find(
          (cloverPoint) => cloverPoint.entityType === 'venue' && cloverPoint.entityId === venueId,
        );

        if (!isVenueIdInCloverPointsState && cloverConfigIdFromVenue) {
          dispatch({ type: 'cleanup-clover-points', operation: 'reset' });
          const cloverPoints = await getAvailableCloverPoints(cloverConfigIdFromVenue);
          dispatch({
            type: 'new-venue-clover-points',
            operation: 'add',
            payload: {
              entityType: 'venue',
              cloverPoints,
              entityId: venueId,
            },
          });
        }

        const currentCommerceIdsForCloverPoints = state.reduce((acc, point) => {
          if (point.entityType === 'commerce') {
            acc.push(point.entityId);
          }
          return acc;
        }, [] as string[]);

        if (commerceIds) {
          for await (const commerceId of commerceIds) {
            if (!currentCommerceIdsForCloverPoints.includes(commerceId)) {
              const correspondingCommerce = commerces.find((commerce) => commerce.id === commerceId);
              if (correspondingCommerce) {
                const cloverConfigId = correspondingCommerce.paymentConfig?.cloverPointConfigId;
                if (cloverConfigId) {
                  const cloverPoints = await getAvailableCloverPoints(cloverConfigId);
                  dispatch({
                    type: 'new-commerce-clover-points',
                    operation: 'add',
                    payload: {
                      entityType: 'commerce',
                      cloverPoints,
                      entityId: correspondingCommerce.id,
                    },
                  });
                }
              }
            }
          }
        }
      }
      dispatch({
        type: 'delete-duplicate-clover-points-in-list',
        operation: 'delete',
      });
    },
    [venueId, selectedCommerceIds],
  );

  React.useEffect(() => {
    settingPossibleCloverPoints(venueId, selectedCommerceIds);
  }, [venueId, selectedCommerceIds]);

  const cloverPointsForSelect = (state: ReducerState[], pickedCloverPointSerials?: string[]) => {
    const reduceStateToCloverPoints = state
      .reduce((acc, value) => {
        if (value.cloverPoints && value.cloverPoints.length > 0) {
          acc.push(value.cloverPoints);
        }
        return acc;
      }, [] as Array<CloverPoint[]>)
      .flatMap((cloverPoint) => cloverPoint);

    if (pickedCloverPointSerials) {
      const reducingSelectedCloverPointsToIds = reduceStateToCloverPoints.reduce((acc, value) => {
        const cloverPointSerialInPicked = pickedCloverPointSerials.includes(value.serial);
        if (cloverPointSerialInPicked) {
          acc.push(value.id);
        }
        return acc;
      }, [] as string[]);
      return reducingSelectedCloverPointsToIds;
    }

    return reduceStateToCloverPoints.map((cloverPoint) => {
      return `${cloverPoint.description} - Serial: ${cloverPoint.serial}`;
    });
  };

  const handlePickedCloverPoints = (pickedItems: string[]) => {
    setPickedCloverPoints(pickedItems);
    const serialOfPickedCloverPoints = pickedItems.map((value) => value.split(' - Serial: ')[1]);
    const reducingSelectedCloverPointsToIdsByPickedOnes = cloverPointsForSelect(state, serialOfPickedCloverPoints);
    callback(reducingSelectedCloverPointsToIdsByPickedOnes);
  };

  return (
    <>
      {state.length > 0 && state[0]?.entityId !== initialStateForCloverPoints[0]?.entityId && (
        <View style={{ flex: 1 }}>
          <Text style={styles.selectLabel}>{t('clover-points')}</Text>
          <RNMultiSelect
            placeholder={t('select-clover-point')}
            data={cloverPointsForSelect(state)}
            selectedItems={pickedCloverPoints}
            onSelectedItemsChange={(value) => handlePickedCloverPoints(value)}
            inputStyle={styles.input}
            searchBarPlaceholder={t('search')}
            modalTitleStyle={{ color: venueColor }}
            checkedItemsColor={venueColor}
            searchBarStyle={{
              minHeight: 45,
              maxHeight: 'auto',
              backgroundColor: '#f8f4f4',
              fontFamily: 'circular-std',
              fontSize: 16,
              textTransform: 'capitalize',
              color: '#3f3a40',
            }}
            renderClearButton={(onClearAll) => renderActionButton(onClearAll, 'clear')}
            renderSaveButton={(onApply) => renderActionButton(onApply, 'save')}
          />
        </View>
      )}
    </>
  );
};
